import { safe } from '../utils/contrast.js';
import { atributoDeBoton } from '../utils/boton-estilo.js';
import { t } from '../utils/i18n.js';
import { escapeHtml, attrJson } from '../utils/dom.js';

/**
 * RESERVA-LIVE — el bloque que reserva DE VERDAD (lectura y escritura contra el ERP).
 *
 * ¿Por qué un bloque NUEVO y no una variante de `form`/`reservas`?
 *   El bloque `reservas` de hoy es una línea (`renderFormLike(..., {kind:'reservas'})`) y su
 *   envío acaba en un `Lead`: un formulario SIN estado. Este flujo, en cambio, es una MÁQUINA
 *   DE ESTADOS (personas → día → hora → datos → confirmación) que consulta huecos reales y
 *   escribe una reserva. Meterlo en `form.js` contaminaría el bloque de contacto de todo el
 *   builder. `reservas` se queda INTACTO y sigue siendo el default → ninguna web publicada se
 *   rompe; quien quiera disponibilidad en vivo inserta este bloque.
 *
 * ── QUÉ SE HORNEA Y QUÉ SE PIDE ────────────────────────────────────────────────
 * El sitio publicado es HTML ESTÁTICO: todo dato horneado MIENTE hasta la próxima publicación.
 * Por eso la frontera es explícita:
 *   · HORNEADO en `data-booking-config` (POLÍTICA, cambia una vez al año): días cerrados,
 *     turnos, antelación mínima/máxima, duración, mínimo/máximo de comensales. Sirve para
 *     pintar en gris los días imposibles ANTES de que llegue la red (primer pintado a 0 ms).
 *   · HORNEADO en `data-live-params` (IDENTIDAD de la conexión): `{restaurant_id, zones[]}` y
 *     nada más. Turnos, cupo y aforo viven en el ERP: NO se duplican en datos_json_builder.
 *   · SE PIDE al servidor (única fuente de verdad): qué huecos quedan y el "quedan X".
 * `data-live="availability"` marca la sección como hidratable (carril genérico: mañana lo
 * reusan carta viva, horarios u ocupación sin inventar otro mecanismo).
 *
 * ── DÓNDE VIVE LA INTERACCIÓN ──────────────────────────────────────────────────
 * Aquí NO hay un solo `on*` inline: el saneo del HTML publicado los borra (gotcha #13). Todo
 * el asistente lo mueve `core/reserva-live-runtime.js` por DELEGACIÓN DE CLASE (`.rl-*`),
 * enganchado desde `core/block-actions.js` → corre igual en el editor y en el publicado.
 *
 * ── PARIDAD EDITOR ↔ PUBLICADO (gotcha #12) ────────────────────────────────────
 * `data-booking-config` / `data-live` / `data-live-params` los emite ESTE render, dentro de su
 * propio `<section>`. No son inyecciones de render.js, así que `compileSiteHtml` (que llama al
 * MISMO render) los reproduce por construcción: no hay nada que replicar ni que se pueda
 * olvidar. El chrome del editor (sello "vista previa") va marcado `.editor-only` → lo elimina
 * `compileSiteHtml` al publicar.
 *
 * ── NADA HARDCODEADO ───────────────────────────────────────────────────────────
 * Todo lo que ve el visitante sale de props (editable inline) o de UI_LABELS vía `t()`:
 *   · props → título, subtítulo, política, etiqueta del botón y el COPY DE LOS CINCO ESTADOS
 *     (hay hueco / no hay hueco / lista de espera / hoy cerrado / error). El cliente los
 *     reescribe y el traductor los traduce.
 *   · t()   → el chrome fijo (rótulos de paso, "Volver", "Código"…), determinista y sin coste
 *     LLM (languages.py::_RESERVA_LABELS).
 * OJO: en el sitio publicado el ESTADO lo decide el ENDPOINT, jamás el prop. El prop solo
 * aporta la REDACCIÓN de cada estado.
 *
 * Modelo de datos (props):
 *   { variant:'v1'|'v2', title, subtitle, text (política),
 *     submit_label, success_msg,
 *     state_ok, state_full, state_waitlist, state_closed, state_error,
 *     booking: { party_min, party_max, party_default, party_options[],
 *                closed_weekdays[0..6], closed_dates['YYYY-MM-DD'],
 *                shifts:[{name, from:'13:00', to:'16:00', step:15}],
 *                min_hours, max_days, duration },
 *     source: { mode:'live'|'lead', restaurant_id, zones:[{id,name}] },
 *     fields: [{ label, type, placeholder, required, role }],
 *     recipient_type:'email'|'whatsapp', recipient }
 *
 * `source.mode`:
 *   · 'live' → asistente conectado al motor del ERP (huecos reales + reserva real).
 *   · 'lead' (DEFECTO) → exactamente lo de hoy: el asistente recoge los datos y el envío
 *     acaba en `/site_lead/` con "te confirmamos en breve". Es también el destino de la
 *     DEGRADACIÓN: timeout, error de red, 5xx o `module_off` caen aquí y el cliente NUNCA
 *     pierde una reserva por un fallo nuestro.
 *
 * Variantes:
 *   v1 = Asistente centrado (copy arriba, widget debajo).
 *   v2 = Split: copy a la izquierda, asistente a la derecha.
 */

// ── Defaults de POLÍTICA ─────────────────────────────────────────────────────
// Son un punto de partida editable, no una verdad: en modo 'live' el servidor revalida SIEMPRE
// (nunca se confía en el hueco que manda el navegador). Un restaurante nuevo con la política por
// defecto ya puede recibir peticiones; ajustar días/turnos es cosa del panel del bloque.
export const BOOKING_DEFAULTS = {
    party_min: 1,
    party_max: 12,
    party_default: 2,
    party_options: [1, 2, 3, 4, 5, 6, 8],
    closed_weekdays: [],          // 0 = domingo … 6 = sábado
    closed_dates: [],             // ['2026-12-25'] festivos/cierres puntuales
    shifts: [
        { name: 'Comida', from: '13:00', to: '15:30', step: 30 },
        { name: 'Cena', from: '20:00', to: '22:30', step: 30 },
    ],
    min_hours: 2,                 // antelación mínima (horas)
    max_days: 60,                 // antelación máxima (días)
    duration: 90,                 // minutos que ocupa la mesa
};

/** Rol SEMÁNTICO de un campo de contacto. NUNCA se manda la etiqueta visible al servidor:
 *  `translatable.js` la traduce a 9 idiomas y una reserva identificada por "Teléfono" se
 *  rompería el día que alguien publica en catalán. El rol es estable y no se traduce.
 *
 *  `alergias` es un rol de PLENO DERECHO y no un caso de `notas`: aterriza en
 *  `Reservation.alergias`, que el modelo declara DATO DE SALUD (no sale por correo, no se
 *  publica, no se cachea). Mientras el campo rotulado "Alergias" viajó como `notas`, ese
 *  tratamiento especial no se aplicaba a nada: la columna se quedaba vacía y el dato acababa
 *  en las notas, que sí van íntegras en el aviso al local. El texto libre sigue en `notas`.
 *
 *  `ROLE_BY_TYPE` NO mapea nada a `alergias` a propósito (un `textarea` sin rol sigue siendo
 *  `notas`): el rol se declara, no se adivina — adivinarlo convertiría en dato de salud
 *  cualquier campo libre que el cliente añadiera. */
const ROLE_BY_TYPE = { tel: 'telefono', email: 'email', textarea: 'notas' };
export const FIELD_ROLES = ['nombre', 'telefono', 'email', 'notas', 'alergias'];

/** Etiqueta de UI de respaldo por rol: si el cliente vacía la etiqueta de un campo, este no se
 *  queda mudo. Solo para los roles que tienen clave propia en UI_LABELS. */
const ROLE_LABEL_KEY = { alergias: 'rl_alergias', notas: 'rl_notes' };

export function roleOf(field, i) {
    const declarado = (field && field.role ? String(field.role) : '').toLowerCase();
    if (FIELD_ROLES.indexOf(declarado) !== -1) return declarado;
    const tipo = (field && field.type ? String(field.type) : 'text').toLowerCase();
    if (ROLE_BY_TYPE[tipo]) return ROLE_BY_TYPE[tipo];
    // El primer campo de texto de un formulario de reserva es, por convención, el nombre.
    return i === 0 ? 'nombre' : '';
}

// Slug estable para el `name` de los campos sin rol conocido (mismo criterio que form.js).
const slugify = (s) => (s || '').toString().toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

// `attrJson` vive en utils/dom.js (SSOT desde T1b: también lo usa el bloque `menu`). Se re-exporta
// aquí para no romper a quien lo importe de este módulo.
export { attrJson };

/** Política efectiva = defaults + lo que el cliente haya ajustado (sin mutar props). */
export function bookingConfig(p) {
    const b = (p && typeof p.booking === 'object' && p.booking) || {};
    const cfg = Object.assign({}, BOOKING_DEFAULTS, b);
    cfg.party_options = Array.isArray(b.party_options) && b.party_options.length
        ? b.party_options.slice(0, 12) : BOOKING_DEFAULTS.party_options;
    cfg.shifts = Array.isArray(b.shifts) && b.shifts.length ? b.shifts : BOOKING_DEFAULTS.shifts;
    cfg.closed_weekdays = Array.isArray(b.closed_weekdays) ? b.closed_weekdays : [];
    cfg.closed_dates = Array.isArray(b.closed_dates) ? b.closed_dates : [];
    return cfg;
}

/** Origen de datos del bloque: {mode, restaurant_id, zones[]}. Sin él ⇒ modo 'lead'. */
export function bookingSource(p) {
    const s = (p && typeof p.source === 'object' && p.source) || {};
    const zonas = Array.isArray(s.zones) ? s.zones : (Array.isArray(p && p.zones) ? p.zones : []);
    return {
        mode: s.mode === 'live' ? 'live' : 'lead',
        restaurant_id: s.restaurant_id != null ? String(s.restaurant_id) : '',
        // `days` (días con servicio de ESA sala, convención `Date.getDay()`) se conserva si viene:
        // es lo que deja al calendario tachar los días imposibles en cuanto se elige la sala, sin
        // esperar a la red. Se normaliza a números y solo se emite si tiene algo — una lista vacía
        // significaría "esta sala no abre nunca" y tacharía el mes entero.
        zones: zonas
            .map((z) => (typeof z === 'string' ? { id: z, name: z } : z))
            .filter((z) => z && (z.name || z.id))
            .map((z) => {
                const out = { id: String(z.id != null ? z.id : z.name), name: String(z.name || z.id) };
                const dias = Array.isArray(z.days)
                    ? z.days.map(Number).filter((n) => n >= 0 && n <= 6) : [];
                if (dias.length) { out.days = dias; }
                return out;
            }),
    };
}

// Iconos SVG del chrome del widget (nunca emojis). Heredan color por currentColor.
const ICON = {
    users: '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/></svg>',
    calendar: '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>',
    clock: '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><polyline points="12 7 12 12 15 14"/></svg>',
    check: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>',
    eye: '<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z"/><circle cx="12" cy="12" r="3"/></svg>',
};

// Un paso del indicador superior. `n` es ordinal (no contenido): no pasa por props.
const stepPill = (n, key, label, active) => `
    <li class="rl-step${active ? ' is-active' : ''}" data-step-pill="${key}">
        <span class="rl-step-n" aria-hidden="true">${n}</span>
        <span class="rl-step-label">${escapeHtml(label)}</span>
    </li>`;

// Un campo de contacto. Etiqueta EDITABLE (data-field="label"), control real no editable y
// data-field-role con el rol semántico que viajará al servidor.
const renderField = (field, i, index) => {
    const rawType = (field.type || 'text').toString().toLowerCase();
    const isTextarea = rawType === 'textarea';
    const inputType = ['text', 'email', 'tel'].indexOf(rawType) !== -1 ? rawType : 'text';
    const role = roleOf(field, i);
    const fname = role || slugify(field.name) || slugify(field.label) || `campo-${i + 1}`;
    const fid = `rl-${index}-f${i}`;
    const req = field.required ? ' required aria-required="true"' : '';
    const ph = escapeHtml(field.placeholder || '');
    const auto = inputType === 'email' ? ' autocomplete="email"' : (inputType === 'tel' ? ' autocomplete="tel"' : '');
    const control = isTextarea
        ? `<textarea id="${fid}" name="${fname}" data-field-role="${escapeHtml(role || fname)}" class="rl-input rl-textarea" rows="3" placeholder="${ph}"${req}></textarea>`
        : `<input id="${fid}" name="${fname}" data-field-role="${escapeHtml(role || fname)}" type="${inputType}" class="rl-input" placeholder="${ph}"${req}${auto}>`;
    const etiqueta = field.label || (ROLE_LABEL_KEY[role] ? escapeHtml(t(ROLE_LABEL_KEY[role])) : '');
    return `
        <div class="rl-field sortable-item" data-item-index="${i}" data-array-name="fields" data-array="fields" data-index="${i}">
            <label class="rl-label" for="${fid}" data-field="label" contenteditable="true">${etiqueta}</label>
            ${control}
        </div>`;
};

export const reservaLiveBlock = (rawProps, index = 0) => {
    const p = safe(rawProps);
    const variant = p.variant === 'v2' ? 'v2' : 'v1';
    const cfg = bookingConfig(p);
    const src = bookingSource(p);

    const fields = Array.isArray(p.fields) ? p.fields : [];
    const title = p.title || '';
    const subtitle = p.subtitle || '';
    const policy = p.text || '';
    const submitLabel = p.submit_label || t('rl_confirm');
    const successMsg = p.success_msg || '';

    // COPY de los cinco estados: 100% editable. En el publicado el estado lo decide el
    // endpoint; esto solo dice CÓMO se cuenta cada uno.
    const estados = [
        ['ok', p.state_ok || ''],
        ['full', p.state_full || ''],
        ['waitlist', p.state_waitlist || ''],
        ['closed', p.state_closed || ''],
        ['error', p.state_error || ''],
    ];

    const headHtml = `
        <h2 class="rl-title" data-field="title" contenteditable="true">${title}</h2>
        <p class="rl-subtitle" data-field="subtitle" contenteditable="true">${subtitle}</p>`;

    const zonasHtml = src.zones.length ? `
        <div class="rl-row rl-row-zones">
            <span class="rl-row-label">${escapeHtml(t('rl_zone'))}</span>
            <div class="rl-zones" role="radiogroup" aria-label="${escapeHtml(t('rl_zone'))}">
                ${src.zones.map((z, i) => `<button type="button" class="rl-chip rl-zone${i === 0 ? ' is-active' : ''}" data-zone="${escapeHtml(z.id)}" role="radio" aria-checked="${i === 0 ? 'true' : 'false'}">${escapeHtml(z.name)}</button>`).join('')}
            </div>
        </div>` : '';

    // Copy de estado: se pintan LOS CINCO. En el sitio servido el runtime enseña solo el que
    // decide el ENDPOINT (`hidden`); en el canvas del editor los enseña todos, con su etiqueta,
    // para que el cliente pueda reescribirlos sin tener que provocar cada situación (llenar el
    // restaurante para poder editar el "no hay mesa" no es una opción). La etiqueta es chrome
    // del editor: va FUERA del contenteditable (si fuera dentro, el extractor se la tragaría
    // como parte del texto) y marcada .editor-only → no viaja al publicado.
    const ROTULO_ESTADO = {
        ok: 'Hay hueco', full: 'No hay hueco', waitlist: 'Solo lista de espera',
        closed: 'Hoy cerrado', error: 'Error de conexión',
    };
    const estadosHtml = estados.map(([k, txt]) => `
        <div class="rl-state-row">
            <span class="rl-state-tag editor-only">${escapeHtml(ROTULO_ESTADO[k])}</span>
            <p class="rl-state-copy" data-state-copy="${k}" data-field="state_${k}" contenteditable="true" hidden>${txt}</p>
        </div>`).join('');

    const widgetHtml = `
    <div class="rl-widget" data-mode="${src.mode}" data-rl-index="${index}">
        <div class="rl-preview-badge editor-only" title="Aquí ves los pasos desplegados para poder editar todos los textos. En tu web se ven de uno en uno. La disponibilidad es real; el envío está desactivado.">${ICON.eye}<span>Vista previa · pasos desplegados, envío desactivado</span></div>

        <ol class="rl-steps" aria-hidden="true">
            ${stepPill(1, 'people', t('rl_people'), true)}
            ${stepPill(2, 'day', t('rl_pick_day'), false)}
            ${stepPill(3, 'time', t('rl_pick_time'), false)}
        </ol>

        <div class="rl-panel" data-step="people">
            <div class="rl-row">
                <span class="rl-row-label">${ICON.users}<span>${escapeHtml(t('rl_people'))}</span></span>
                <div class="rl-people" role="radiogroup" aria-label="${escapeHtml(t('rl_people'))}"></div>
            </div>
            ${zonasHtml}
        </div>

        <div class="rl-panel" data-step="day" hidden>
            <div class="rl-cal-head">
                <button type="button" class="rl-cal-nav rl-cal-prev" aria-label="${escapeHtml(t('rl_back'))}">&#8249;</button>
                <span class="rl-cal-month" aria-live="polite"></span>
                <button type="button" class="rl-cal-nav rl-cal-next" aria-label="${escapeHtml(t('rl_next'))}">&#8250;</button>
            </div>
            <div class="rl-cal" role="group" aria-label="${escapeHtml(t('rl_pick_day'))}"></div>
        </div>

        <div class="rl-panel" data-step="time" hidden>
            <div class="rl-row">
                <span class="rl-row-label">${ICON.clock}<span>${escapeHtml(t('rl_pick_time'))}</span></span>
                <span class="rl-picked-day"></span>
            </div>
            <div class="rl-slots" role="group" aria-label="${escapeHtml(t('rl_pick_time'))}"></div>
            <div class="rl-alts" hidden></div>
        </div>

        <form class="rl-panel rl-form" data-step="details" hidden>
            <div class="rl-summary"></div>
            <div class="rl-fields">${fields.map((f, i) => renderField(f, i, index)).join('')}</div>
            ${/* Casillas de trona / acceso sin escaleras. Nace VACÍO y oculto: lo rellena el
                  runtime SOLO si la política del restaurante las pide (dato vivo del endpoint
                  `slots`). Que no se hornee es justo lo que permite encender el interruptor en
                  el ERP y verlo en la web publicada sin volver a publicarla. */''}
            <div class="rl-extras" hidden></div>
            ${/* CONSENTIMIENTO del campo de alergias (dato de salud, art. 9 RGPD). Nace VACÍO y
                  oculto por el mismo motivo que `.rl-extras`: solo tiene sentido si el comensal
                  ESCRIBE algo, y eso lo sabe el runtime, no el render. El texto sale de `t()`
                  (chrome, no copy del negocio) y el propio runtime crea esta caja si no la
                  encuentra, así que un sitio publicado ANTES de este cambio también pide el
                  consentimiento sin volver a publicarlo. */''}
            <div class="rl-consent" hidden></div>
            <p class="rl-policy" data-field="text" contenteditable="true">${policy}</p>
            <div class="rl-hp" aria-hidden="true"><label>No rellenar<input type="text" name="_hp" tabindex="-1" autocomplete="off"></label></div>
            <button type="submit" class="rl-submit btn-action" data-field="submit_label" contenteditable="true"${atributoDeBoton(p, 'submit_label')}>${submitLabel}</button>
        </form>

        <div class="rl-panel rl-done" data-step="done" hidden tabindex="-1" role="dialog" aria-modal="false">
            <span class="rl-done-icon" aria-hidden="true">${ICON.check}</span>
            <h3 class="rl-done-title">${escapeHtml(t('rl_confirmed'))}</h3>
            <dl class="rl-done-data"></dl>
            <p class="rl-done-msg" data-field="success_msg" contenteditable="true">${successMsg}</p>
            ${/* Acciones sobre la reserva YA hecha. Ocultas hasta que el runtime sepa que hay una
                  reserva recordada (localStorage) y con qué llave se puede anular: "Anular" usa la
                  MISMA puerta firmada que el enlace del correo (/site_reserva/cancelar/), así que
                  no hay una segunda lógica de cancelación que se pueda desincronizar. */''}
            <div class="rl-done-actions">
                <button type="button" class="rl-cancel" hidden>${escapeHtml(t('rl_cancel'))}</button>
                <button type="button" class="rl-again" hidden>${escapeHtml(t('rl_again'))}</button>
            </div>
        </div>

        <div class="rl-status" data-rl-status>
            ${estadosHtml}
            <p class="rl-live" role="status" aria-live="polite"></p>
        </div>

        <div class="rl-nav">
            <button type="button" class="rl-back" hidden>${escapeHtml(t('rl_back'))}</button>
            <button type="button" class="rl-next btn-action"${atributoDeBoton(p, 'submit_label')}>${escapeHtml(t('rl_next'))}</button>
        </div>
    </div>`;

    // El `style` va PRIMERO en el tag: render.js/compileSiteHtml buscan `style="` con regex no
    // greedy y así siempre encuentran el de verdad, no un fragmento del JSON de configuración.
    const baseStyle = `--bg:${p.bgColor}; --text:${p.textColor}; --accent:${p.accentColor}; --accent-contrast:${p.accentContrast}; --font:'${p.fontFamily || 'Inter'}'; padding: 100px 8%;`;
    const liveAttrs = src.mode === 'live'
        ? ` data-live="availability" data-live-params="${attrJson({ restaurant_id: src.restaurant_id, zones: src.zones })}"`
        : '';
    // Destino del camino DEGRADADO (y del modo 'lead'): el mismo par de metadatos que usa el
    // bloque form para /site_lead/. Van en la <section> —no en el <form>— porque el runtime los
    // lee desde el widget hacia arriba y así sirven a los dos caminos.
    const recipientType = p.recipient_type === 'whatsapp' ? 'whatsapp' : 'email';
    const leadAttrs = ` data-recipient-type="${recipientType}" data-recipient="${escapeHtml(p.recipient || '')}"`;
    const openTag = `<section id="bloque-reserva-live-${index}" class="reveal variant-${variant} rl-section" style="${baseStyle}"${liveAttrs}${leadAttrs} data-booking-config="${attrJson(cfg)}">`;

    if (variant === 'v2') {
        return `
    ${openTag}
        <div class="rl-wrap rl-split">
            <div class="rl-head">${headHtml}</div>
            <div class="rl-widget-col">${widgetHtml}</div>
        </div>
    </section>`;
    }

    return `
    ${openTag}
        <div class="rl-wrap">
            <div class="rl-head rl-head-center">${headHtml}</div>
            ${widgetHtml}
        </div>
    </section>`;
};
